import { cloneDeep } from 'lodash-es';

import {Store} from '@ngrx/store';
import {BaseService} from './base.service';
import {LocalComponentStore} from '../store/local-component.store';
import {IAppState} from '../store/app.store';
import {
    fetchInternalOrderCodes,
    fetchInternalOrderCodesSuccess,
    fetchInternalOrderCodesFailure,
} from '../store/actions/internal-order.actions';
import {
    selectInternalOrderState,
    selectInternalOrderCodesData,
    selectInternalOrderCodes,
} from '../store/selectors/internal-order.selectors';
import {Injectable, Injector} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import { catchError, distinctUntilChanged, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EMPTY, forkJoin, from, Observable, of, Subject } from 'rxjs';
import {AppSettings} from '../app.settings';
import {ToastrService} from 'ngx-toastr';
import dayjs from 'dayjs';

import {CacheModel} from '../models/cache.model';
import {Response} from '../models/response.model';
import {Order} from '../models/order.model';
import {OrderItem} from '../models/order-item.model';
import {OrderWindow} from '../models/order-window.model';
import {OnDemand} from '../models/on-demand.model';
import {CostCenter} from '../models/cost-center.model';
import {BRANCH_TYPE, ECOMMERCE_TYPE, PREORDER_TYPE, SUMMARY_TYPE} from '../constants/order-types';
import {ProductService} from './product.service';
import {AuthService} from './auth.service';
import {EntityService} from './entity.service';
import {CreditCard} from '../models/credit-card.model';
import {ApiOrderService} from './api-order.service';
import {QUOTE_STATE} from '../constants/order-states';
import {Router} from '@angular/router';
import {GoogleAnalytics} from './google-analytics.service';
import {ACTIVE_ORDER_WINDOW_ID, LocalStorageService} from './local-storage.service';
import {AddressService} from './address.service';
import { findModifiedAddress, isWSLROrder, orderAddressKey } from '../shared/helpers';
import {allocationGroupIdOf, OrderPresetAddress} from '../models/order-preset-address';
import {getObjectKeyByValue, isEmptyArray, updateItemInArray} from '../shared/utils';
import {BudgetService} from './budget.service';
import {IError} from '../models/error.model';
import {UNKNOWN_ERROR_CODE} from '../constants/error.codes';
import {OrderHistory} from '../models/order-history.model';
import {AvailableOrderWindow} from '../interfaces/order-window.interface';
import {MultiTenantService} from './multi-tenant.service';
import { DATE_FORMAT } from '../constants/date-format';
import {Address} from '../models/address.model';
import {ModalService} from './modal.service';
import {AlertType} from '../enums/alert-type';
import {AddressType} from '../enums/address-type';
import {DistributionList} from '../models/distribution-list.model';
import {Product} from '../models/product.model';
import {PunchoutDataModel} from '../models/punchout-data.model';
import {PnpBudgetService} from './pnp-budget.service';
import {FilterGroup} from '../models/filter-group.model';


export const ORDER_ITEM_PAGE_SIZE = 20;

interface OrderState {
    activeOrderWindow: OrderWindow;
    orderWindows: OrderWindow[];
    switchingOrderWindow: boolean;
    historyScrollY: number;
    historyOrders: Record<string, OrderHistory[]>;
    historyCounts: Record<string, number>;
    inited: boolean;
    orders: Order[];
    selectedOrder: Order;
    currentOrderTypeLabel: string;
    currentOrderType: string;
    cartSelectedOrdersType: string;
    fetchingOrderWindows: boolean;
    fetchingInitialOrderData: boolean;
}

@Injectable()
export class OrderService extends BaseService {

    public id: unknown;

    // Id of the window whose catalogs/orders have actually been fetched. Kept separate from
    // activeOrderWindow (which init sets before the first switch) so the guard in
    // switchActiveOrderWindow$ can skip redundant re-switches without skipping the initial fetch.
    private lastSwitchedOrderWindowId: number = null;

    private loadingOrderItems = new Set<number>();

    private readonly reset$ = new Subject<void>();

    protected trackingCache: CacheModel;
    public isUpdatingTaxExempt = false;

    private componentStore = new LocalComponentStore<OrderState>({
        activeOrderWindow: null,
        orderWindows: [],
        switchingOrderWindow: false,
        historyScrollY: 0,
        historyOrders: {PREORDER_TYPE: [], ECOMMERCE_TYPE: [], SUMMARY_TYPE: [], SEARCH: []},
        historyCounts: {PREORDER_TYPE: 0, ECOMMERCE_TYPE: 0, SUMMARY_TYPE: 0, SEARCH: 0, },
        inited: false,
        orders: [],
        selectedOrder: null,
        currentOrderTypeLabel: '',
        currentOrderType: '',
        cartSelectedOrdersType: '',
        fetchingOrderWindows: false,
        fetchingInitialOrderData: false,
    });

    /** Reactive streams for the formerly-observable state. */
    readonly activeOrderWindow$ = this.componentStore.select(s => s.activeOrderWindow);
    readonly inited$ = this.componentStore.select(s => s.inited);
    readonly currentOrderType$ = this.componentStore.select(s => s.currentOrderType);

    /**
     * Derived streams for the formerly-computed values that were exposed through `toRx`.
     * Re-uses the existing synchronous getters so the values are identical; ComponentStore's
     * `select` recomputes on every state change and de-dupes by reference, matching the prior
     * MobX `observe`-based emissions.
     */
    readonly activeOrder$ = this.componentStore.select(() => this.activeOrder);
    readonly activeBWOrder$ = this.componentStore.select(() => this.activeBWOrder);
    readonly activeODOrder$ = this.componentStore.select(() => this.activeODOrder);

    /** Synchronous accessors preserve the previous public observable fields. */
    get activeOrderWindow(): OrderWindow { return this.componentStore.snapshot(s => s.activeOrderWindow); }
    set activeOrderWindow(v: OrderWindow) { this.componentStore.patchState({ activeOrderWindow: v }); }

    get orderWindows(): OrderWindow[] { return this.componentStore.snapshot(s => s.orderWindows); }
    set orderWindows(v: OrderWindow[]) { this.componentStore.patchState({ orderWindows: v }); }

    get switchingOrderWindow(): boolean { return this.componentStore.snapshot(s => s.switchingOrderWindow); }
    set switchingOrderWindow(v: boolean) { this.componentStore.patchState({ switchingOrderWindow: v }); }

    get historyScrollY(): number { return this.componentStore.snapshot(s => s.historyScrollY); }
    set historyScrollY(v: number) { this.componentStore.patchState({ historyScrollY: v }); }

    get historyOrders(): Record<string, OrderHistory[]> { return this.componentStore.snapshot(s => s.historyOrders); }
    set historyOrders(v: Record<string, OrderHistory[]>) { this.componentStore.patchState({ historyOrders: v }); }

    get historyCounts(): Record<string, number> { return this.componentStore.snapshot(s => s.historyCounts); }
    set historyCounts(v: Record<string, number>) { this.componentStore.patchState({ historyCounts: v }); }

    get inited(): boolean { return this.componentStore.snapshot(s => s.inited); }
    set inited(v: boolean) { this.componentStore.patchState({ inited: v }); }

    get orders(): Order[] { return this.componentStore.snapshot(s => s.orders); }
    set orders(v: Order[]) { this.componentStore.patchState({ orders: v }); }

    get selectedOrder(): Order { return this.componentStore.snapshot(s => s.selectedOrder); }
    set selectedOrder(v: Order) { this.componentStore.patchState({ selectedOrder: v }); }

    get currentOrderTypeLabel(): string { return this.componentStore.snapshot(s => s.currentOrderTypeLabel); }
    set currentOrderTypeLabel(v: string) { this.componentStore.patchState({ currentOrderTypeLabel: v }); }

    get currentOrderType(): string { return this.componentStore.snapshot(s => s.currentOrderType); }
    set currentOrderType(v: string) { this.componentStore.patchState({ currentOrderType: v }); }

    get cartSelectedOrdersType(): string { return this.componentStore.snapshot(s => s.cartSelectedOrdersType); }
    set cartSelectedOrdersType(v: string) { this.componentStore.patchState({ cartSelectedOrdersType: v }); }

    get fetchingOrderWindows(): boolean { return this.componentStore.snapshot(s => s.fetchingOrderWindows); }
    set fetchingOrderWindows(v: boolean) { this.componentStore.patchState({ fetchingOrderWindows: v }); }

    get fetchingInitialOrderData(): boolean { return this.componentStore.snapshot(s => s.fetchingInitialOrderData); }
    set fetchingInitialOrderData(v: boolean) { this.componentStore.patchState({ fetchingInitialOrderData: v }); }

    private modalService;


    public punchoutDataReceived: Subject<PunchoutDataModel> = new Subject<PunchoutDataModel>();

    constructor(
        private api: ApiOrderService,
        protected http: HttpClient,
        protected appSettings: AppSettings,
        protected toastr: ToastrService,
        private productService: ProductService,
        private authService: AuthService,
        private entityService: EntityService,
        private router: Router,
        private googleAnalytics: GoogleAnalytics,
        private localStorage: LocalStorageService,
        private addressService: AddressService,
        private budgetService: BudgetService,
        private multiTenantService: MultiTenantService,
        private injector: Injector,
        private readonly pnpBudgetService: PnpBudgetService,
        private store: Store<IAppState>,
    ) {
        super('/order', http, appSettings, toastr);

        this.trackingCache = new CacheModel();
        this.trackingCache.expiration = 3600;

        this.modalService = this.injector.get<ModalService>(ModalService);

        this.addressService.distributionListChanged.subscribe((dl: DistributionList | number) => {

            let dlId = 0;

            if (!dl ) {
                return;
            }

            if (typeof dl === 'number') {
                dlId = dl;
            } else {
                dlId = dl.id;
            }

            // get list of orders to be updated
            const ordersToBeUpdated = this.orders.filter(o => o.hasDistributionListById(dlId));
            if (isEmptyArray(ordersToBeUpdated)) {
                // no need to update orders
                return;
            }

            const ordersIdsToBeUpdated: number[] = ordersToBeUpdated.map( o => o.id);

            // get unique list of products ids to be update
            const productsIdsToBeUpdated = new Set<number>();
            ordersToBeUpdated.forEach( o => {
                o.productsList.forEach(p => {
                    if (p.id) {
                        productsIdsToBeUpdated.add(p.id);
                    }
                })
            });

            const productsIdsToBeUpdatedArray = Array.from(productsIdsToBeUpdated) || [];


            this.refetchProductsByIds(productsIdsToBeUpdatedArray).pipe(
              switchMap( () => {
                  // fetch orders from server
                  return this.fetchOrders(ordersIdsToBeUpdated);
              }),
              tap(receivedOrders => {
                  if (isEmptyArray(receivedOrders)) {
                      return [];
                  }
                  let newOrders =  [...this.orders];
                  for (const o of receivedOrders) {
                      newOrders = updateItemInArray<Order>(newOrders, o);
                  }
                  this.setOrders(newOrders);

                  // update products in cache
                  for (const order of receivedOrders) {
                      this.productService.updateProductFromOrder(order);
                  }

              })
            ).subscribe()
        });
    }

    private refetchProductsByIds(productsIds: number[]): Observable<Product[]>  {
        if (isEmptyArray(productsIds)) {
            return of([]);
        }
        return this.productService.getProductsByIds(
            productsIds,
            { force: true }
        );
    }

    // get order windows list and put into store
    private getOrderWindows(fetchAllOrderWindows = false): Observable<unknown> {
        if (this.authService.canBuyingWindow) {
            this.fetchingOrderWindows = true;
            return this.api.fetchOrderWindows(fetchAllOrderWindows).pipe(
               switchMap((orderWindows: OrderWindow[]) => {
                   this.fetchingOrderWindows = false;
                   this.orderWindows = orderWindows;
                   if (!this.activeOrderWindow) {
                       this.activeOrderWindow = this.restoreActiveOrderWindow(orderWindows);

                       if (!this.activeOrderWindow) {
                           this.activeOrderWindow = this.findLastActiveOrder();
                       }
                   }

                   if (this.activeOrderWindow) {
                       return this.switchActiveOrderWindow$(this.activeOrderWindow);
                   }

                   return of(true);
               })
            );
        } else {
            return of(true);
        }
    }


    public switchActiveOrderWindowById(id: number): OrderWindow {
        if (!id) {
            return;
        }

        const window = this.orderWindows.find(ow => ow.id === id);
        if (!window) {
            this.router.navigateByUrl('/404', {skipLocationChange: true});
        }

        this.switchActiveOrderWindow(window);
        return window;
    }

    public switchActiveOrderWindow(orderWindow?: OrderWindow) {
        if (!orderWindow) {
            orderWindow = this.findLastActiveOrder();
        }

        if (orderWindow) {
            this.switchActiveOrderWindow$(orderWindow).subscribe();
        }
    }

    public switchActiveOrderWindow$(orderWindow: OrderWindow, force = false): Observable<unknown> {

      // Skip redundant re-switches to the already-loaded window. Called from ~11 nav/init sites;
      // this guard prevents each one from re-running the full catalogs + active-orders fetch.
      // lastSwitchedOrderWindowId (not activeOrderWindow alone) keeps the initial fetch intact.
      if (!force && orderWindow?.id === this.activeOrderWindow?.id
        && this.lastSwitchedOrderWindowId === orderWindow?.id) {
        return of(this.activeOrderWindow);
      }

      this.switchingOrderWindow = true;
        // get active orders

        return forkJoin(
            [
                this.productService.onActiveOrderWindowChanged(orderWindow.id),
                this.getActiveOrders(orderWindow).pipe(
                    tap(() => {
                      this.setActiveOrderWindow(orderWindow);
                      this.switchingOrderWindow = false;
                      this.lastSwitchedOrderWindowId = orderWindow.id;
                    }),
                )
            ]
        )
    }

    public switchActiveOrder(order: Order): Observable<Order> {
        return this.getOrder(order.id).pipe(
            switchMap(resOrder => {

                if (!resOrder || resOrder.is_active) {
                    return of(resOrder);
                }

                // do not  make  active  order of  other  users
                if (resOrder.user_id !== this.authService.user.id) {
                    return of(resOrder);
                }

                return this.api.makeActiveOrder(resOrder.id).pipe(
                    map(result => {
                        if (result) {
                            const previousActiveOrders = this.findActiveOrdersByType(order.type);
                            previousActiveOrders.forEach(o => {
                                o.is_active = false;
                                this.updateOrderList(o);
                            });

                            const newActive = new Order(resOrder);
                            newActive.is_active = true;

                            this.updateOrderList(newActive);

                            return newActive;
                        }

                        return resOrder;
                    }),
                );

            })
        )


    }

    // 1. fetch  OD orders
    // 2. fetch BW orders if necessary
    // 3  set inited state  to true
    public fetchAllOrders(): void {
        if (this.inited || this.fetchingInitialOrderData) {
            return;
        }

        this.fetchingInitialOrderData = true;

        const odParams = {id: 0} as OnDemand;

        this.getActiveOrders(odParams).pipe(
            switchMap(() => {
                if (!this.activeOrderWindow) {
                    return this.getOrderWindows(true);
                }

                return of(true);
            }),
            takeUntil(this.reset$),
            finalize(() => {
                this.fetchingInitialOrderData = false;
            })
        ).subscribe(() => {
            this.setInited(true);
        });
    }

    public getActiveOrders(property?: OrderWindow | OnDemand): Observable<Order> { //
        if (!this.authService.canSeeOrders) {
            return of(null);
        }

        return this.api.fetchActiveOrders(property)
            .pipe(
                tap(orders => {

                    if (property?.id > 0) {
                        // remove BW  orders
                        this.setOrders(this.orders.filter(o => !o.isByingWindow));
                    }
                    
                    orders.forEach(o => {
                        // o.isWSLR = isWSLROrder(o, this.authService.user);
                        // o.loaded = true;
                        this.updateOrderList(o);
                    });

                    this.setOrders(this.orders);
                }),
                switchMap(() => this.getDefaultODOrder()),
                switchMap(() => this.getDefaultBWOrder()),
            );
    }

    private findActiveOrdersByType(type: string): Order[] {
        return this.orders.filter(o => (o.type === type && o.is_active));
    }

    private getDefaultODOrder(): Observable<Order> {
        if (this.currentODOrders.length === 0) {
            return of(null);
        }

        const order = this.activeODOrder || this.currentODOrders[0];
        return of(order);
        // return this.switchActiveOrder(order);
    }

    private getDefaultBWOrder(): Observable<Order> {
        if (this.currentBWOrders.length === 0) {
            return of(null);
        }

        const order = this.activeBWOrder || this.currentBWOrders[0];

        return of(order);
    }

    // returns last order window with active = true or undefined
    public findLastActiveOrder(): OrderWindow {
        return this.orderWindows.filter(i => i.active === true).pop();
    }

    public findOrderById(id: number, force = false): Observable<Order> {
        if (!force) {
            const order = this.orders.find(o => o.id === id);
            if (order && order.loaded) {
                return of(order);
            }
        }
        return this.api.fetchOrder(id);
    }

    private isOrdersOD(orders: Order[]): boolean  {
        return Boolean(orders.filter(o => (o.type === ECOMMERCE_TYPE && o.state === QUOTE_STATE)).length);
    }

    private setOrderItem(order: Order) {
        if (!order) {
            return;
        }
        order.items.forEach(i => {
            i.order_id = order.id;
            i.order_type = order.type;
        });
    }

    public updateOrderList(order: Order | number) {
        if (!order) {
            return;
        }

        if (typeof order === 'number') {
            this.setOrders(this.orders.filter(o => o.id !== order));
            return;
        }


        // check if order is WSLR
        order.isWSLR = isWSLROrder(order, this.authService.user);
        // order.loaded = true;


        const idx = this.orders.findIndex((o) => o.id === order.id);
        this.setOrders(
          idx !== -1
            ? Object.assign([...this.orders], { [idx]: order })
            : [...this.orders, order]
        );
    }

    private fetchOrderEntity(order: Order): Observable<Order> {
        if (order && order.entity_id > 0 && !order.entity) {
            return this.entityService.getEntityWithLocations(order.entity_id).pipe(
                map(entity => {
                    order.entity = entity;
                    return order;
                })
            )
        }

        return of(order);
    }

    private fetchOrderDecisionPoints(order: Order): Observable<Order> {
        if (!order || isWSLROrder(order, this.authService.user) || !order.isDecisionPoint || order.decisionPointData) {
            return of(order);
        }

        return this.productService.searchDecisionPoints(order.attr_decision_point_name).pipe(map(  result => {
            if (!isEmptyArray(result)) {
                order.decisionPointData = result[0];
            }
            return order;
        }))

    }

    private fetchBudgets(order: Order): Observable<Order> {
        if (!order || !order.canEdit) {
            return of(order);
        }
        return this.budgetService.getBudgetsByOrder(order);
    }

    public fetchPresetAddresses(order: Order): Observable<Order> {
        if (!order || !order.canEdit) {
            return of(order);
        }

        order = this.fetchDistributionList(order);

        return this.addressService.getPresetAddressesForOrder(order).pipe(
            map( (addresses) => {
                order.presetAddresses = addresses;
                return order;
            })
        );
    }

    private fetchDistributionList(order: Order): Order {
        if (!order || !order.canEdit) {
            return order;
        }

        order.distributionList = this.addressService.findDistributionListById(order.distributionListId);
        return order;
    }

    public getOrder(
        id: number = null,
        force = false
    ): Observable<Order> {
        return this.findOrderById(id, force).pipe(
            switchMap(order =>
                this.fetchOrderEntity(order)
            ),
            switchMap(order =>
                this.fetchPresetAddresses(order)
            ),
            tap(resOrder => {
                if (!resOrder) {
                    return;
                }

                const existingOrder = this.orders.find(
                    order => order.id === resOrder.id
                );

                const isCurrentActiveBWOrder =
                    existingOrder?.type === PREORDER_TYPE &&
                    existingOrder?.is_active === true &&
                    Number(existingOrder?.window_id) ===
                    Number(this.activeOrderWindowID);

                // Preserve active status during BW order hydration to avoid clearing activeBWOrder.
                if (isCurrentActiveBWOrder) {
                    resOrder.is_active = true;
                }

                resOrder.loaded = true;
                this.setOrderItem(resOrder);
                this.updateOrderList(resOrder);
            })
        );
    }

    public fetchOrderFullInfo(order: Order): Observable<Order> {
        if (!order) {
            return of(order);
        }

        if (order.loaded  && order.isLazy !== true) {
            return of(order);
        }
        return this.getOrder(order.id,  true);
    }

    // the order items endpoint returns items with only product_id (no embedded product),
    // so re-hydrate item.product from the product cache, falling back to /products/for-ids
    private enrichOrderItemsWithProducts(items: OrderItem[]): Observable<OrderItem[]> {
        if (isEmptyArray(items)) {
            return of(items || []);
        }

        const productIds = Array.from(
            new Set(
                items
                    .map(item => item.product_id)
                    .filter((id): id is number => id != null)
            )
        );

        if (productIds.length === 0) {
            return of(items);
        }

        return this.productService.getProductsByIds(productIds).pipe(
            map(products => {
                const productsById = new Map<number, Product>(
                    products.map(product => [product.id, product])
                );

                items.forEach(item => {
                    const product = productsById.get(item.product_id);

                    if (product) {
                        item.attachProduct(product);
                        // Patch product sku client_sku_number with fresh DB value from item skus
                        // when ES forids_skus_static has a stale/null value
                        if (item.skus?.length && product.skus?.length) {
                            item.skus.forEach(itemSku => {
                                if (!itemSku.client_sku_number) { return; }
                                const productSku = product.skus.find(s => Number(s.id) === itemSku.sku_id);
                                if (productSku && !productSku.client_sku_number) {
                                    productSku.client_sku_number = itemSku.client_sku_number;
                                }
                            });
                        }
                    }
                });

                return items;
            })
        );
    }

    // method to get short order info without items, budgets, addresses etc.
    // Used for order list and order details page when we need to update only part of order info without refetching whole order
    public getLazyOrderInfo(id?: number): Observable<Order> {
        return forkJoin([
            this.api.fetchShortOrderInfo(id),
            this.api.fetchOrderItems(id, `${ORDER_ITEM_PAGE_SIZE}-0`)
        ]).pipe(
            switchMap(([orderInfo, itemResult]) => {
                if (!orderInfo) {
                    return of(null);
                }

                const receivedItems = itemResult?.items || [];
                const totalItemCount = itemResult?.count || 0;

                return this.enrichOrderItemsWithProducts(
                    receivedItems
                ).pipe(
                    map(fetchedItems => {
                        fetchedItems.forEach(item => {
                            item.order_id = orderInfo.id;
                            item.order_type = orderInfo.type;
                        });

                        orderInfo.items = fetchedItems;
                        orderInfo.item_count = totalItemCount;
                        return orderInfo;
                    })
                );
            }),
          // Handle failures here rather than in every caller. Log for diagnostics, surface a
          // toast, and complete normally with null - the same value callers already get when
          // the order info request comes back empty - so their next-handler runs and clears
          // the page spinner instead of leaving it running forever.
          catchError(err => {
              console.error('OrderService::getLazyOrderInfo failed', err);
              this.toastr.error('Could not load the order. Please try again.');
              return of(null);
          })
        );
    }

    // method to get order items only, used for order details page when we need to update only order items without refetching whole order
    public getLazyOrderItems(order: Order, range?: string, filterOptionIds: number[] = []): Observable<Order> {
        if (!order) {
            return of(order);
        }

        if (this.loadingOrderItems.has(order.id)) {
            return of(order);
        }

        const totalItems = order.item_count || 0;
        const actualItems = order.items?.length || 0;

        if (actualItems >= totalItems) {
            return of(order);
        }

        this.loadingOrderItems.add(order.id);

        const requestedRange =
            range || `${ORDER_ITEM_PAGE_SIZE}-${actualItems}`;

        return this.api.fetchOrderItems(order.id, requestedRange, filterOptionIds).pipe(
            takeUntil(this.reset$),
            switchMap(result => {
                if (!result || isEmptyArray(result.items)) {
                    return of(order);
                }

                return this.enrichOrderItemsWithProducts(result.items).pipe(
                    map(items => {
                        items.forEach(item => {
                            item.order_id = order.id;
                            item.order_type = order.type;
                        });

                        const existingItemIds = new Set(
                            (order.items || []).map(item => item.id)
                        );

                        const newItems = items.filter(
                            item => !existingItemIds.has(item.id)
                        );

                        order.items = [
                            ...(order.items || []),
                            ...newItems
                        ];

                        if (typeof result.count === 'number') {
                            order.item_count = result.count;
                        }

                        // only refresh orders that are already tracked in the
                        // active list - historical orders opened from account
                        // pages must not be added to it
                        if (this.orders.some(o => o.id === order.id)) {
                            this.updateOrderList(order);
                        }

                        return order;
                    })
                );
            }),
            catchError(error => {
                console.error(
                    'OrderService::getLazyOrderItems failed',
                    error
                );

                this.toastr.error(
                    'Could not load more items. Please try again.'
                );

                return of(order);
            }),
            finalize(() => {
                this.loadingOrderItems.delete(order.id);
            })
        );
    }

    // we need to recalculate order financials and item count as with layzy loading it cannot be done on client side
    public refetchOrderFinancials(order: Order): Observable<Order> {
        if (!order ) {
            return of(order);
        }
        return this.api.fetchShortOrderInfo(order.id).pipe(
          map( result => {
            if (!result) {
               return order;
            }

            order.financials = {...result.financials };
            order.item_count = result.item_count;
            order.currency = result.currency;
            order.loaded = true;
            return new Order(order);
          }),
          tap(resOrder => {
              this.updateOrderList(resOrder);
          })
        )
    }

    public refetchOrderInfoWithItems(order: Order): Observable<Order> {
        if (!order) {
            return of(order);
        }

        const currentlyLoadedItems = order.items?.length || 0;
        const range =
            `${Math.max(ORDER_ITEM_PAGE_SIZE, currentlyLoadedItems)}-0`;

        return forkJoin([
            this.api.fetchShortOrderInfo(order.id),
            this.api.fetchOrderItems(order.id, range)
        ]).pipe(
            takeUntil(this.reset$),
            switchMap(([orderInfo, itemResult]) => {
                if (!orderInfo || !itemResult || !Array.isArray(itemResult.items)) {
                    return of(order);
                }

                return this.enrichOrderItemsWithProducts(itemResult.items).pipe(
                    map(items => {
                        items.forEach(item => {
                            item.order_id = orderInfo.id;
                            item.order_type = orderInfo.type;
                        });

                        orderInfo.items = items;
                        orderInfo.item_count = itemResult.count ?? 0;

                        return orderInfo;
                    })
                );
            }),
            tap(updatedOrder => {
                if (updatedOrder && updatedOrder !== order) {
                    this.updateOrderList(updatedOrder);
                }
            }),
            catchError(error => {
                console.error(
                    'OrderService::refetchOrderInfoWithItems failed',
                    error
                );

                // refreshOrder() is user-initiated, so the stale order returned
                // below must not look like a successful refresh
                this.toastr.error(
                    'Could not refresh the order. Please try again.'
                );

                return of(order);
            })
        );
    }

    getOrderHistory(filter: { type?: string } & Record<string, unknown> = null, paging: string = '10-0'):
        Observable<{ orders: OrderHistory[], count: number, type: string }> {
        return this.api.fetchOrderHistory(filter, paging).pipe(
            map( result => ({...result, type: filter.type}))
        )
    }

    public getAvailableOrderWindows(): Observable<AvailableOrderWindow[]> {
        if (!this.authService.canBuyingWindow) {
            return of([]);
        }
        return this.api.getAvailableOrderWindows();
    }
    // getOrderHistoryCount(type: string): number {
    //     return this.api.getOrderHistoryCount(type);
    // }


    public createFakeOrder(type: string): Order {

        console.log('temporal order is created.');
        const order = new Order({
            id: -1, is_active: true, items: [], items_count: 0, type, state: QUOTE_STATE,
            user: this.authService.user, isNotReal: true, loaded: true,
            user_id: this.authService.user.id,
        });

        this.updateOrderList(order);
        return order;

    }


    // Order level methods
    createOrder(type: string = null, orderOptions: { orderLabel?: string; entity_id?: number } = {}): Observable<Order> {
        if (!type) {
            type = this.currentOrderType;
        }

        if (!this.authService.canOrder || (type === PREORDER_TYPE && !(this.activeOrderWindow && this.activeOrderWindow.active))) {

            console.log('Limited order is created.');

            let order = this.orders.find(o => o.id === 0 && o.type === type);
            if (!order) {
                order = new Order({
                    id: 0, is_active: true, items: [], items_count: 0, type, state: QUOTE_STATE,
                    user: this.authService.user,
                    user_id: this.authService.user?.id
                });
                this.updateOrderList(order);
            }
            return of(order);
        }

        const windowID = (type === PREORDER_TYPE) ? this.activeOrderWindowID : null;
        const body: Record<string, unknown> = {
            window_id: windowID,
            attr_cart_name: orderOptions.orderLabel ? orderOptions.orderLabel : this.getDefaultOrderName(type),
            attr_attention: this.getDefaultAttention()

        };

        if (orderOptions.entity_id) {
            body.entity_id = orderOptions.entity_id;
        }

        return this.api.createOrder(body).pipe(
            switchMap( order => {
               if (!order) {
                   return of(order);
               }
               return this.getOrder(order.id, true);
            }),
            tap(order => {
                if (order) {

                    if (type === PREORDER_TYPE || type === ECOMMERCE_TYPE) {
                        // make previous order not active
                        const active = type === PREORDER_TYPE ? this.activeBWOrder : this.activeODOrder;
                        if (active) {
                            const previousActive = new Order(active);
                            previousActive.is_active = false;
                            this.updateOrderList(previousActive);
                        }

                        const newOrder = new Order(order);
                        newOrder.is_active = true;
                        this.updateOrderList(newOrder);
                    }

                }
            })
        );
    }

    updateCostCenter(order: Order, costCenter: CostCenter): Observable<Order> {
        if (!order || !costCenter) {
            return of(null);
        }
        if (costCenter) {
            costCenter.internal_order = '' + order.id;
        }

        return this.api.updateCostCenter(order.id, costCenter).pipe(
            map(result => {
                if (result['errorCode']) {
                    order.error = result as IError;
                    return order;
                }

                if (result === true) {
                    order.setPaymentMethod(costCenter);
                    order.error = null;
                    return order;
                }

                order.error = {errorCode: UNKNOWN_ERROR_CODE};
                return order;
            }),
            tap( retOrder => {
                if (retOrder) {
                    this.updateOrderList(retOrder);
                }
            })
        );
    }

    updateCreditCard(order: Order, creditCard: CreditCard): Observable<Order> {
        if (!order || !creditCard) {
            return of(null);
        }

        return this.api.setOrderAttribute(order.id, 'credit_card', {id: creditCard.id, label: creditCard.label}).pipe(
            map(result => {
                if (result === true) {
                    order.setPaymentMethod(creditCard);
                    order.error = null;
                } else {
                    order.error = {errorCode: UNKNOWN_ERROR_CODE};
                }
                return order;
            })
        );
    }

    public removeCreditCardFromOrder(order: Order): Observable<Order> {
        return this.api.removeCreditCardFromOrder(order).pipe(
            map(result => {
                if (result) {
                    order.creditCard = null;
                    order.error = null;
                } else {
                    order.error = {errorCode: UNKNOWN_ERROR_CODE};
                }
                return order;
            })
        );
    }


    public setOrderAttributes(order: Order, attributes: Record<string, unknown>, skipFetchOrder = false): Observable<Order> {
        if (!attributes) {
            return of(order);
        }

        return this.api.setOrderAttributes(order.id, attributes).pipe(
            switchMap(result => {
                if (result) {
                    if (!skipFetchOrder) {
                        return this.getOrder(order.id, true);
                    }
                    return of(order)

                }
                return of(null);
            })
        );
    }


    public updateCreditCardByOrderId(orderId: number, creditCard: CreditCard): Observable<Order> {
        if (!orderId) {
            return of(null);
        }
        return this.getOrder(orderId).pipe(switchMap(order => {
            return this.updateCreditCard(order, creditCard);
        }));
    }


    /**
     * update order entity, returns updated order
     * @param orderId - order id
     * @param entityId - entity id
     */
    updateEntity(order: Order, entityId: number): Observable<Order> {
        return this.api.updateOrderEntity(order.id, entityId).pipe(
            switchMap(retOrder => {
                return this.fetchOrderWSLREntity(retOrder)
            })
        );
    }

    delete(orderID: number): void {
        this.api.deleteOrder(orderID)
          .pipe(
            switchMap( result => {
                return this.pnpBudgetService.fetchPnpBudgets().pipe( map(() => result))
            })
          ).subscribe(result => {
            if (result) {
                this.updateOrderList(orderID);
                this.toastr.success('Your cart has been removed.');
            }
        });
    }

    printInvoice(orderID: number): Observable<unknown> {
        return this.api.getInvoice(orderID, '/print');
    }

    pdfInvoice(orderID: number): Observable<unknown> {
        return this.api.getInvoice(orderID, '/pdf');
    }

    emailInvoice(orderID: number, email: string): Observable<unknown> {
        return this.http.post<Response>(this.apiURL + '/' + orderID + '/invoice/email', {email: email}, {headers: this.xsrfTokenHeader})
            .pipe(
                map(response => {
                    return response.data;
                }),
                catchError(this.handleError('OrderService::emailInvoice', []))
            );
    }

    // Item level methods

    getOrderItem(order: Order, itemID: number) {
        this.api.getItem(order.id, itemID).subscribe();
    }

    createOrderItem(order: Order, item: OrderItem): Observable<OrderItem> {

        // set internal order id for deliveries
        this.setInternalOrderId(item);

        return this.api.createItem(order, item).pipe(
            switchMap(orderItem => {
                if (!orderItem) {
                    return of(orderItem);
                }
                this.googleAnalytics.addToShoppingCart(order, orderItem);
                this.updateOrderList(order);

                // fire-and-forget: refreshing the product cache shouldn't delay the returned result
                const productId = orderItem.product_id ?? item.product_id;
                this.productService.refreshProductCacheForOrderItem(productId, orderItem.product).subscribe();
                return of(orderItem);
            })
        );
    }

    updateOrderItem(order: Order, item: OrderItem): Observable<Order> {
        const orderItemID = item.id;
        // set internal order id for deliveries
        this.setInternalOrderId(item);
        return this.api.updateItem(order, orderItemID, item).pipe(
            switchMap(retOrder => {
                if (!retOrder) {
                    return of(null);
                }
                this.googleAnalytics.updateShoppingCart(retOrder, item);
                this.updateOrderList(retOrder);

                // fire-and-forget: refreshing the product cache shouldn't delay the returned result
                const orderItem = retOrder.items.find(i => i.id === orderItemID);
                const productId = orderItem?.product_id ?? item.product_id;
                this.productService.refreshProductCacheForOrderItem(productId, orderItem?.product).subscribe();
                return of(retOrder);
            })
        );
    }

    deleteOrderRestrictedItems(order: Order): Observable<Order> {
        if (!order) {
            return of(null);
        }
        return this.api.deleteRestrictedOrderItems(order.id).pipe(
            map(retOrder => {
                if (retOrder) {
                    this.updateOrderList(retOrder);
                    return retOrder;
                }
                return null;
            })
        );
    }


    // skipProductRefresh: pass true when the caller already forces its own product refetch
    // after delete (e.g. order-item.service.ts), to avoid a redundant round-trip
    deleteOrderItem(order: Order, orderItem: OrderItem, skipProductRefresh = false): Observable<Order> {
        if (!order || !orderItem) {
            return of(null);
        }
        const orderItemId = orderItem.id;
        const productId = orderItem.product_id;

        return this.api.deleteItem(order, orderItemId).pipe(
            switchMap(retOrder => {
                if (!retOrder) {
                    return of(null);
                }

                if (isEmptyArray(retOrder.items)) {
                    // reset order currency
                    retOrder.currency = null;
                }

                this.googleAnalytics.removeFromShoppingCart(retOrder, orderItem);
                this.updateOrderList(retOrder);  // update order list

                if (!skipProductRefresh) {
                    // deleting an item changes the product's inventory/aggregated quantities, so
                    // refetch the product into cache (in the background, fire-and-forget); pass no
                    // fresh product since the deleted item's product is now stale
                    this.productService.refreshProductCacheForOrderItem(productId).subscribe();
                }

                return of(retOrder);
            })
        );
    }

    public deleteAddresses(order: Order, addresses: OrderPresetAddress[]): Observable<Order> {
        // A delivery-derived row can reach here without an address_type - the API omits it and
        // getAddress() has no fallback, which is why addressListsEqual keys off address book objects.
        // Such a row cannot be identified in the body: JSON.stringify drops the undefined key and the
        // endpoint is left with an address_id alone, which by this contract means every allocation
        // group of it. Skip it rather than aim a destructive call at half an identity.
        const identifiableAddresses = (addresses || []).filter(a => a?.address_type != null);

        if (!order || isEmptyArray(identifiableAddresses)) {
            return of(null);
        }

        const requests = identifiableAddresses.map(a => {
            const allocationGroupId = allocationGroupIdOf(a);

            // the group is left out for the address types that never carry one, rather than sent empty:
            // a row without a group stands for the whole address
            return this.api.removeAddress(order.id, {
                address_id: a.id,
                address_type: a.address_type,
                ...(allocationGroupId != null ? {allocation_group_id: allocationGroupId} : {}),
            });
        });

        return forkJoin(requests).pipe(
            map(response => {
                // take the last response
                if (isEmptyArray(response)) {
                    return null;
                }
                return response[response.length - 1];
            }),
        )
    }

    public updateOrderTaxExempt(order: Order, taxExempt: boolean): Observable<Order> {

        this.isUpdatingTaxExempt = true;
        return this.setOrderAttributes(order, {tax_exempt: taxExempt.toString()}).pipe(
            switchMap(result => {
                if (result) {
                    // re-fetch the order to get correct taxes
                    return this.getOrder(order.id, true);
                }
                // failed update
                return of(null);
            }),
            tap(() => this.isUpdatingTaxExempt = false)
        );
    }

    checkout(order: Order): Observable<Order> {
        if (!order) {
            return of(order);
        }


        this.googleAnalytics.checkout(order);
        return this.api.checkout(order.id).pipe(
            map( result => {

                // special case for punchout orders
                if (result instanceof PunchoutDataModel) {
                    this.punchoutDataReceived.next(result);
                    order.error = null;
                    return null;
                }


                if ( typeof  result === 'boolean'  && result === false) {
                    order.error = {errorCode: UNKNOWN_ERROR_CODE };
                }

                if (result['errorCode']) {
                    order.error = result as IError;
                }

                return order;
            }),
            tap(retOrder => {
                if (retOrder && !retOrder.error) {
                    // remove the completed cart from the in-memory store so the header
                    // (MobX computeds over `orders`) stops showing it as an active cart
                    this.updateOrderList(order.id);
                    this.googleAnalytics.purchase(order);
                    this.toastr.success('Order has been submitted');
                }
            })
        );
    }


    setInited(val: boolean) {
        this.inited = val;
    }


    // async setActiveOrder(order: Order) {
    //     if (order) {
    //         if (order.type === ECOMMERCE_TYPE) {
    //             this.setActiveODOrder(order);
    //         } else {
    //             this.setActiveBWOrder(order);
    //         }
    //
    //
    //         this.setActive(order.id).pipe(take(1)).subscribe(noop => {
    //             this.resetActive(order.id, order.type);
    //             this.setCurrentOrders();
    //             this.setCurrentActiveOrder(order);
    //         });
    //     }
    // }

    setSelectedOrder(order: Order) {
        this.selectedOrder = order;
    }

    get odCount() {
        return this.availableODOrders.filter(o  => !o.isNotReal).length;
    }

    get bwCount() {
        return this.availableBuyingWindowOrders.filter(o  => !o.isNotReal).length;
    }

    get branchCount() {
        return this.branchOrders.filter(o  => !o.isNotReal).length;
    }

    get summaryCount() {
        return this.summaryOrders.filter(o  => !o.isNotReal).length;
    }

    get bwTotalCount() {
        return this.bwCount + this.branchCount + this.summaryCount;
    }

    public getApiUrl() {
        return this.apiURL;
    }


    /**
     * refresh tracking_numbers in order delivires
     * result is cached
     * @param order
     */
    getTrackingData(order: Order): Observable<Order> {
        return this.http.get<Response>(`${this.apiURL}/${order.id}/tracking-state`)
            .pipe(
                map(response => {
                    if (!isEmptyArray(response.data)) {
                        return this.mapTracksToShipment(order, response.data);
                    }
                    return order;
                }),
                catchError(this.handleError('OrderService::tracking-state', null))
            );

    }


    /**
     * map track_numbers to related delivery items
     * @param order
     * @param trackData, contains tracking-numbers
     */
    private mapTracksToShipment(order: Order, trackData: { shipment_id: number; tracking_number: string }[]): Order {
        if (isEmptyArray(trackData) || isEmptyArray(order.items)) {
            return;  // empty track number
        }

        const orderItems  = order.items.map( i => new OrderItem(i));
        let updatedTrackingData = false;
        orderItems.forEach(item => {
            if (item.deliveries) {

                item.deliveries.forEach(delivery => {
                    const shipment = trackData.find(data => data.shipment_id === delivery.id);
                    if (shipment) {
                        delivery.tracking_number = shipment.tracking_number;
                        updatedTrackingData = true;
                    }
                });
            }
        });

        if (updatedTrackingData) {
            order.items = orderItems;
        }
        return order;
    }


    setActiveOrderWindow(orderWindow?: OrderWindow) {
        this.activeOrderWindow = orderWindow;

        // save to local storage
        if (this.activeOrderWindow) {
            this.localStorage.setItem(ACTIVE_ORDER_WINDOW_ID, '' + this.activeOrderWindow.id);
        }
    }

    // for BW order user can't add/modify order items in case oder window is not defined or not acvtive
    canEditOrderItems(order: Order): boolean {
        if (order.type !== PREORDER_TYPE) {
            return true;
        }

        return this.activeOrderWindow && this.activeOrderWindow.active;
    }

    public canPurchase(order: Order, orderItem: OrderItem): boolean {
        if (!order) {
            return false;
        }

        if (!this.canEditOrderItems(order)) {
            return false;
        }

        if (orderItem.productHasCustomization && !orderItem.isCustomized) {
          return false;
        }

        return orderItem.canPurchase;

    }

    setCurrentOrderType(type: string): void {
        if (!type || this.currentOrderType === type) {
            return;
        }

        this.currentOrderType = type;
    }

    get currentOrders(): Order[] {
        if (!this.currentOrderType) {
            return [];
        }
        const orders  = this.currentOrderType === PREORDER_TYPE ? this.currentBWOrders : this.currentODOrders;
        // filter  out fake orders
        return orders.filter(o => !o.isNotReal);
    }


    // reset all orders - should be called after logout
    reset() {
        this.reset$.next();
        this.activeOrderWindow = null;
        this.lastSwitchedOrderWindowId = null;
        this.orderWindows = [];
        this.setOrders([]);
        this.selectedOrder = null;
        this.currentOrderType = '';

        // clear the in-flight guards so fetchAllOrders() and the per-order lazy
        // item loads can run again for the next user without a full page reload
        this.inited = false;
        this.fetchingInitialOrderData = false;
        this.fetchingOrderWindows = false;
        this.loadingOrderItems.clear();

        this.api.clear();
    }


    // promocode

    applyPromoCode(order: Order, promoCode: string): Observable<Order> {
        return this.api.applyPromoCode(order, promoCode).pipe(
            map(retOrder => {
                if (retOrder) {
                    this.updateOrderList(retOrder);
                    this.toastr.success('Promo Code has been applied');
                }
                return retOrder;
            })
        );
    }


    revokePromoCode(order: Order): Observable<Order> {
        return this.api.revokePromoCode(order).pipe(
            map(retOrder => {
                if (retOrder) {
                    this.updateOrderList(retOrder);
                    this.toastr.success('Promo Code has been removed');
                }
                return retOrder;
            })
        );
    }

    private fetchOrderWSLREntity(order: Order): Observable<Order> {
        if (isWSLROrder(order, this.authService.user) && !order.entity) {
            return this.entityService.getEntityWithLocations(order.entity_id).pipe(
                map(entity => {
                    order.entity = entity;
                    this.updateOrderList(order);
                    return order;
                })
            );
        } else {
            return of(order);
        }
    }


    setOrders(orders: Order[]): void {
        this.orders = orders;
    }

    get activeOrderWindowID(): number {
        return this.activeOrderWindow ? this.activeOrderWindow.id : 0;
    }

    get availableODOrders(): Order[] {
        return this.orders.filter(o => (o.type === ECOMMERCE_TYPE && o.state === QUOTE_STATE));
    }

    get currentODOrders(): Order[] {
        return this.availableODOrders.filter(o => this.authService.canEditOrder(o));
    };

    get availableBuyingWindowOrders(): Order[] {
        return this.orders.filter(o => o.type === PREORDER_TYPE);
    }

    get buyingWindowOrders(): Order[] {
        return this.availableBuyingWindowOrders.filter( o => this.authService.canEditOrder(o));
    }


    get branchOrders(): Order[] {
        return this.orders.filter(o => o.type === BRANCH_TYPE);
    };

    get summaryOrders(): Order[] {
        return this.orders.filter(o => o.type === SUMMARY_TYPE);
    };

    get currentBWOrders(): Order[] {
        if (this.authService.isWholeSalerMultiple && this.branchOrders.length > 0) {
            return this.buyingWindowOrders.concat(this.branchOrders);
        } else {
            return this.buyingWindowOrders;
        }
    }


    public get activeODOrder(): Order {
        const currentUserId  = this.authService.user?.id;
        return this.currentODOrders.find(o => o.is_active && o.user_id === currentUserId);
    }

    public get activeBWOrder(): Order {
        const currentUserId  = this.authService.user?.id;
        return this.currentBWOrders.find(o => o.is_active && o.user_id === currentUserId);
    };

    // Method to check not empty order summary
    get notEmptyOrderSummary(): boolean {
        return !!this.activeOrderWindow && this.activeBWOrder && this.activeBWOrder.item_count > 0;
    }

    // Method to check empty order summary
    get emptyOrderSummary(): boolean {
        return !!this.activeOrderWindow && (!this.activeBWOrder || !this.activeBWOrder.item_count);
    }

    public get activeOrder(): Order {
        // if (!this.currentOrderType) {
        //     return null;
        // }
        //
        return this.currentOrderType === PREORDER_TYPE ? this.activeBWOrder : this.activeODOrder;
    }

    // wrappers for state streams
    public getActiveOrderWindow(): Observable<OrderWindow> {
        return this.activeOrderWindow$;
    }

    public getDefaultOrderName(type: string): string {
        const creationDate = dayjs().format(DATE_FORMAT.LONG_FORMAT);
        let orderType = '';
        if (type === ECOMMERCE_TYPE) {
            orderType =  this.multiTenantService.onDemandLabel;
        } else if (type === PREORDER_TYPE) {
            // bw order
            orderType = this.activeOrderWindow ? this.activeOrderWindow.label
              : this.multiTenantService.orderWindowLabel;
        }

        return `${orderType} ${creationDate}`;

    }

    public getDefaultAttention(): string {
        if (!this.authService.user) {
            return '';
        }
        return `${this.authService.user.first_name} ${this.authService.user.last_name}`;
    }


    public isInitialized(): Observable<boolean> {
        return this.inited$;
    }

    public get buyingWindowChanged(): Observable<OrderWindow> {
        // Only emit when the window actually changes, so subscribers (header, header-navigation,
        // home programs) don't re-fetch overlapping catalogs on no-op activeOrderWindow re-sets.
        return this.activeOrderWindow$.pipe(
            distinctUntilChanged((a: OrderWindow, b: OrderWindow) => a?.id === b?.id)
        );
    }

    public get orderTypeChanged(): Observable<string> {
        return this.currentOrderType$;
    }

    public get  activeOrderChanged(): Observable<Order> {
        return this.activeOrder$;
    }

    private restoreActiveOrderWindow(orderWindows: OrderWindow[]): OrderWindow {
        if (!this.activeOrderWindow) {
            // get from localstorage
            const activeOrderId = +this.localStorage.getItem(ACTIVE_ORDER_WINDOW_ID);
            const orderWindow = orderWindows.find(o => o.id === activeOrderId); // make sure order window exists in the list
            if (orderWindow) {
                this.activeOrderWindow = orderWindow;
            }
        }

        return this.activeOrderWindow;
    }

    public get currentOrderWindowId(): number {
        if (this.currentOrderType === PREORDER_TYPE) {
            return this.activeOrderWindowID;
        }

        return 0;
    }

    getOrdersByType(type: string): Order[] {
        switch (type) {
            case PREORDER_TYPE:
                return this.currentBWOrders;
            case ECOMMERCE_TYPE:
                return this.currentODOrders;
            case BRANCH_TYPE:
                return this.branchOrders;
            case SUMMARY_TYPE:
                return this.summaryOrders;
            default:
                return [];
        }
    }

    getOrderCountsByType(type: string): number {
        switch (type) {
            case PREORDER_TYPE:
                return this.bwCount;
            case ECOMMERCE_TYPE:
                return this.odCount;
            case BRANCH_TYPE:
                return this.branchCount;
            case SUMMARY_TYPE:
                return this.summaryCount;
            default:
                return 0;
        }
    }

    public get ordersByType(): {type: string, label: string, subLabel: string, orders: Order[]}[] {
        const orderList: {type: string, label: string, subLabel: string, orders: Order[]}[] = [];
        if (!this.inited) {
            return orderList;
        }

        if (this.authService.canBuyingWindow && !isEmptyArray(this.availableBuyingWindowOrders)) {
            orderList.push({ type: PREORDER_TYPE, label: `${this.multiTenantService.orderWindowLabel} Orders`,
                subLabel: 'YOUR ORDERS', orders: this.currentBWOrders});
        }

        if (!isEmptyArray(this.availableODOrders)) {
            orderList.push({ type: ECOMMERCE_TYPE, label: `${this.multiTenantService.onDemandLabel} Orders`,
                subLabel: 'YOUR ORDERS', orders: this.currentODOrders});
        }

        if (this.authService.isWholeSalerMultiple && !isEmptyArray(this.branchOrders)) {
            orderList.push({ type: BRANCH_TYPE, label: 'Branch Orders', subLabel: '', orders: this.branchOrders});
        }

        if ( (this.authService.isWholeSaler || this.authService.isWholeSalerMultiple) && !isEmptyArray(this.summaryOrders)) {
            orderList.push({ type: SUMMARY_TYPE, label: `${this.multiTenantService.projectLabel} Orders`,
                subLabel: '', orders: this.summaryOrders});
        }

        if (!this.cartSelectedOrdersType && !isEmptyArray(orderList)) {
            const odEntry = orderList.find(o => o.type === ECOMMERCE_TYPE);
            const hasRealODOrders = !!odEntry && this.odCount > 0;
            this.cartSelectedOrdersType = hasRealODOrders ? ECOMMERCE_TYPE : orderList[0].type;
        }

        return orderList;

    }


    public get cartSelectedOrders(): {type: string, label: string, subLabel: string, orders: Order[]} {
        if (!this.cartSelectedOrdersType || isEmptyArray(this.ordersByType)) {
            return null;
        }

        return this.ordersByType.find(i => i.type === this.cartSelectedOrdersType);
    }

    public getActiveOrNewOrder(): Observable<Order> {
        if (this.activeOrder) {
            return of(this.activeOrder);
        }

        return this.createOrder();
    }

    // Loads the internal order codes into the store on demand. Idempotent: skips the
    // request if the codes are already loaded or a request is already in flight.
    public getInternalOrderCodes(): void {
        this.store.select(selectInternalOrderState).pipe(
            take(1),
            switchMap(state => {
                if (state.fetching || state.fetched) {
                    return of(null);
                }

                this.store.dispatch(fetchInternalOrderCodes());

                return this.api.getInternalOrderCodes().pipe(
                    take(1),
                    tap(internalOrderData => {
                        if (!internalOrderData) {
                            this.store.dispatch(
                                fetchInternalOrderCodesFailure()
                            );
                            return;
                        }

                        this.store.dispatch(
                            fetchInternalOrderCodesSuccess({
                                codesData: internalOrderData as Record<
                                    string,
                                    string
                                >
                            })
                        );
                    }),
                    catchError(error => {
                        console.error(
                            'OrderService::getInternalOrderCodes failed',
                            error
                        );

                        this.store.dispatch(
                            fetchInternalOrderCodesFailure()
                        );

                        return of(null);
                    })
                );
            })
        ).subscribe();
    }

    // sorted list of billing/internal-order codes for the billing-code dropdown; updates
    // reactively as getInternalOrderCodes() populates the store
    public getInternalOrderCodesList(): Observable<string[]> {
        return this.store.select(selectInternalOrderCodes);
    }

    // update order default addresses
    public updateOrderDefaultAddresses(order: Order, distributionList: DistributionList, addressList: OrderPresetAddress[],
                                       shouldUpdateOrder = false): Observable<Order> {

        let shipmentAddresses: OrderPresetAddress[];
        if (distributionList && distributionList.hasAddresses) {
            shipmentAddresses = [...distributionList.addresses, ...(addressList || [])];
        } else {
            shipmentAddresses = addressList || [];
        }

        // Determine whether addresses actually changed using only address-book Address objects
        // (both sides). Delivery-constructed Address objects from findUniqueShipmentAddresses()
        // can have address_type=undefined when the API omits it, causing key mismatches with
        // address-book objects and false "deleted" detections that trigger the popup incorrectly.
        // This covers all three modes: list view, grid view, and distribution list.
        const addressesModified = !this.addressListsEqual(order.distributionOrPresetAddresses, shipmentAddresses);

        return this.updateShipmentsInOrder(order, shipmentAddresses, shouldUpdateOrder, addressesModified).pipe(
            switchMap( retOrder => {
                if (!retOrder) {
                    return of(null);
                }

                // check if  order preset  update is required
                if (!this.orderPresetsUpdateRequired(order, addressList)) {
                    return this.getOrder(retOrder.id, true);
                }


                // update preset addresses in order settings
                const orderAttributes = {};
                if (isEmptyArray(addressList)) {
                    orderAttributes['preset_addresses'] = [];
                } else {
                    orderAttributes['preset_addresses'] = addressList.filter(address => address != null).map(address => {
                        if (address.isWarehouse) {
                            // the allocation group is what tells two rows of the same warehouse apart,
                            // so it has to be stored per entry rather than per address
                            return {id: address.id, allocation_group_id: allocationGroupIdOf(address)};
                        }
                        if (address.address_type === AddressType.Personal) {
                            return {id: address.id};
                        }
                        return {id: address.id, entity_id: address.entity_id};
                    });
                }
                return this.setOrderAttributes(retOrder, orderAttributes);
            }),
        );
    }

    private orderPresetsUpdateRequired(order: Order, addresses: OrderPresetAddress[]): boolean {
        if (!order) {
            return false;
        }
        const compareResult  =  findModifiedAddress(order.presetAddresses, addresses);
        return compareResult.some( item => item.status !== 'unchanged');
    }


    // Compare two address lists by their order address key (reliable for address-book Address objects).
    private addressListsEqual(a: Address[], b: Address[]): boolean {
        const toKeys = (list: Address[]) =>
            (list || []).filter(x => x != null).map(x => orderAddressKey(x)).sort().join(',');
        return toKeys(a) === toKeys(b);
    }

    //  update or remove  all shipments in order  by specified addresses
    private updateShipmentsInOrder(order: Order, addressList: Address[], shouldUpdateOrder = false, addressesModified = true): Observable<Order> {

        if (!shouldUpdateOrder || !addressesModified) {
            return of(order);
        }

        const clonedAddresses = cloneDeep(addressList);
        const shipmentAddresses = order.findUniqueShipmentAddresses();
        const updateResult  =  findModifiedAddress(shipmentAddresses, clonedAddresses);
        if (updateResult.every( r => r.status === 'unchanged')) {
            // no changes in order shipments
            return of(order);
        }

        // Quantities are never carried from one row to another: a row that leaves the list takes the
        // products ordered under it, and the row that takes its place starts empty. An edited row is
        // reported as a new address plus the old one as deleted, so removing what is gone is all that
        // is needed - nothing here has to recognise the edit as a move.
        const toBeDeletedAddresses = updateResult.filter( r => r.status === 'deleted').map( r => r.address);

        if (isEmptyArray(toBeDeletedAddresses)) {
            // no updates in order are required
            return of(order);
        }

        // A warehouse row is the pair of an address and an allocation group, and any change to that
        // pair carries away the products ordered under it - another group, another warehouse, or the
        // row dropped altogether. What loses them is the group rather than the address, so the pair
        // gets its own wording.
        const warehousePairRemoved = toBeDeletedAddresses.some( address => address?.isWarehouse);
        const message  =  warehousePairRemoved
            ? 'This allocation group has ordered products. Are you sure you want to remove?'
            : 'This address has ordered products. Are you sure you want to remove?';

        return this.showConfirmation('Attention', message).pipe(
            switchMap(  confirmationResult => {
                if (!confirmationResult) {
                    return of(null);
                }
                return this.deleteAddresses(order, toBeDeletedAddresses);
            })
        )
    }

    private showConfirmation(title: string, message: string) {
        if (!message) {
            // skip confirmation
            return of(true);
        }

        return from(this.modalService.showAlert({
            type: AlertType.Confirm,
            class: 'max-width-330',
            headerClass: 'state modal-info modal-warning',
            headerIcon: 'fa-warning',
            title,
            message,
            actionLabel: 'OK'
        }));

    }

    // force fetch  orders from server by ids
    private  fetchOrders(ids: number[]): Observable<Order[]> {
        if (isEmptyArray(ids)) {
            return of([]);
        }

        const requests = ids.map(id => this.getOrder(id, true));
        return forkJoin(requests);
    }


    // set internal order id for deliveries
    private  setInternalOrderId(orderItem: OrderItem) {
        if (!orderItem) {
            return;
        }

        if (isEmptyArray(orderItem.deliveries)) {
            return;
        }

        orderItem.deliveries.forEach(delivery => {
            if (delivery.internal_order) {
                const id = this.findInternalOrderIdByCode(delivery.internal_order);
                if (id) {
                    delivery.internal_order_id = id;
                }
            }
        });


    }

    private findInternalOrderIdByCode(code: string): string {
        // find  id by value from the internal order codes map held in the store
        let codesData: { [id: string]: string } = {};
        this.store.select(selectInternalOrderCodesData).pipe(take(1)).subscribe((data) => {
            codesData = data;
        });
        return getObjectKeyByValue(codesData, code) || '';
    }

    public getOrderFilters(orderId: number): Observable<FilterGroup[]> {
        return this.api.fetchOrderFilters(orderId);
    }

    // Returns the filtered items/count for display only - never mutates the canonical order,
    // which other consumers (checkout, budget-details modal) read via the same Order reference.
    public getFilteredOrderItems(order: Order, filterOptionIds: number[]): Observable<{ items: OrderItem[]; count: number }> {
        if (!order) {
            return of({ items: [], count: 0 });
        }

        const range = `${Math.max(ORDER_ITEM_PAGE_SIZE, order.item_count || 0)}-0`;
        // fetchOrderItemsOrThrow (unlike fetchOrderItems) lets HTTP errors reach catchError below,
        // so a failed request can be told apart from a genuine zero-match filter result.
        return this.api.fetchOrderItemsOrThrow(order.id, range, filterOptionIds).pipe(
            switchMap(result => {
                if (!result || isEmptyArray(result.items)) {
                    return of({ items: [], count: 0 });
                }

                return this.enrichOrderItemsWithProducts(result.items).pipe(
                    map(items => {
                        items.forEach(i => { i.order_type = order.type; });
                        return { items, count: result.count };
                    })
                );
            }),
            catchError(err => {
                // Preserve the caller's last-known filtered view instead of clearing it on a
                // transient failure.
                console.error('OrderService::getFilteredOrderItems failed', err);
                return EMPTY;
            })
        );
    }

}
